import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  Typography,
  FormControlLabel,
  RadioGroup,
  Radio,
  FormControl,
  FormLabel,
  MenuItem,
  Select,
  InputLabel,
  Box,
  Divider,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActions
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import axios from '../utils/axios';
import { useAuth } from '../contexts/AuthContext';

const ParishRegistrationForm = ({ open, onClose, registration = null, onSave }) => {
  const { user } = useAuth();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    family: {
      lastName: '',
      firstNames: '',
      address: {
        street: '',
        city: '',
        state: '',
        zipCode: ''
      },
      contact: {
        homePhone: '',
        cellPhone: '',
        email: ''
      }
    },
    primaryContact: {
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      occupation: '',
      employer: '',
      residency: '',
      primaryLanguage: 'English',
      currentReligion: 'Catholic',
      sacraments: {
        baptized: { received: '', interested: '', where: '', when: '' },
        communion: { received: '', interested: '', where: '', when: '' },
        confirmed: { received: '', interested: '', where: '', when: '' }
      },
      marriage: {
        status: '',
        location: '',
        date: '',
        catholicChurch: ''
      }
    },
    spouse: {
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      occupation: '',
      employer: '',
      primaryLanguage: 'English',
      currentReligion: 'Catholic',
      sacraments: {
        baptized: { received: '', interested: '', where: '', when: '' },
        communion: { received: '', interested: '', where: '', when: '' },
        confirmed: { received: '', interested: '', where: '', when: '' }
      }
    },
    children: [],
    additionalAdults: []
  });

  const steps = ['Family Info', 'Primary Contact', 'Spouse/Family', 'Review'];

  useEffect(() => {
    if (registration) {
      setFormData(registration);
    } else {
      // Reset form for new registration
      setFormData({
        family: {
          lastName: '',
          firstNames: '',
          address: { street: '', city: '', state: '', zipCode: '' },
          contact: { homePhone: '', cellPhone: '', email: '' }
        },
        primaryContact: {
          firstName: '',
          lastName: '',
          gender: '',
          dateOfBirth: '',
          occupation: '',
          employer: '',
          residency: '',
          primaryLanguage: 'English',
          currentReligion: 'Catholic',
          sacraments: {
            baptized: { received: '', interested: '', where: '', when: '' },
            communion: { received: '', interested: '', where: '', when: '' },
            confirmed: { received: '', interested: '', where: '', when: '' }
          },
          marriage: { status: '', location: '', date: '', catholicChurch: '' }
        },
        spouse: {
          firstName: '',
          lastName: '',
          gender: '',
          dateOfBirth: '',
          occupation: '',
          employer: '',
          primaryLanguage: 'English',
          currentReligion: 'Catholic',
          sacraments: {
            baptized: { received: '', interested: '', where: '', when: '' },
            communion: { received: '', interested: '', where: '', when: '' },
            confirmed: { received: '', interested: '', where: '', when: '' }
          }
        },
        children: [],
        additionalAdults: []
      });
    }
    setActiveStep(0);
    setError('');
  }, [registration, open]);

  const handleInputChange = (section, field, value, subField = null) => {
    setFormData(prev => {
      const newData = { ...prev };
      if (subField) {
        if (!newData[section][field]) newData[section][field] = {};
        newData[section][field][subField] = value;
      } else {
        newData[section][field] = value;
      }
      return newData;
    });
  };

  const handleSacramentChange = (section, sacrament, field, value) => {
    setFormData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        sacraments: {
          ...prev[section].sacraments,
          [sacrament]: {
            ...prev[section].sacraments[sacrament],
            [field]: value
          }
        }
      }
    }));
  };

  const addChild = () => {
    const newChild = {
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      primaryLanguage: 'English',
      currentReligion: 'Catholic',
      sacraments: {
        baptized: { received: '', interested: '', where: '', when: '' },
        communion: { received: '', interested: '', where: '', when: '' },
        confirmed: { received: '', interested: '', where: '', when: '' }
      }
    };
    setFormData(prev => ({
      ...prev,
      children: [...prev.children, newChild]
    }));
  };

  const removeChild = (index) => {
    setFormData(prev => ({
      ...prev,
      children: prev.children.filter((_, i) => i !== index)
    }));
  };

  const addAdult = () => {
    const newAdult = {
      firstName: '',
      lastName: '',
      gender: '',
      dateOfBirth: '',
      relationship: '',
      primaryLanguage: 'English',
      currentReligion: 'Catholic',
      sacraments: {
        baptized: { received: '', interested: '', where: '', when: '' },
        communion: { received: '', interested: '', where: '', when: '' },
        confirmed: { received: '', interested: '', where: '', when: '' }
      }
    };
    setFormData(prev => ({
      ...prev,
      additionalAdults: [...prev.additionalAdults, newAdult]
    }));
  };

  const removeAdult = (index) => {
    setFormData(prev => ({
      ...prev,
      additionalAdults: prev.additionalAdults.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError('');

      const endpoint = registration
        ? `/api/parish-registration/${registration._id}`
        : '/api/parish-registration/manual';

      const method = registration ? 'put' : 'post';

      const response = await axios[method](endpoint, formData);

      onSave(response.data);
      onClose();
    } catch (error) {
      console.error('Error saving parish registration:', error);
      setError('Failed to save registration');
    } finally {
      setLoading(false);
    }
  };

  const renderFamilyInfo = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Names"
          value={formData.family.firstNames}
          onChange={(e) => handleInputChange('family', 'firstNames', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={formData.family.lastName}
          onChange={(e) => handleInputChange('family', 'lastName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Street Address"
          value={formData.family.address.street}
          onChange={(e) => handleInputChange('family', 'address', e.target.value, 'street')}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="City"
          value={formData.family.address.city}
          onChange={(e) => handleInputChange('family', 'address', e.target.value, 'city')}
          required
        />
      </Grid>
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth required>
          <InputLabel>State</InputLabel>
          <Select
            value={formData.family.address.state}
            onChange={(e) => handleInputChange('family', 'address', e.target.value, 'state')}
            label="State"
          >
            <MenuItem value="VA">Virginia</MenuItem>
            <MenuItem value="MD">Maryland</MenuItem>
            <MenuItem value="DC">Washington DC</MenuItem>
            <MenuItem value="WV">West Virginia</MenuItem>
            <MenuItem value="NC">North Carolina</MenuItem>
          </Select>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          label="Zip Code"
          value={formData.family.address.zipCode}
          onChange={(e) => handleInputChange('family', 'address', e.target.value, 'zipCode')}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Home Phone"
          value={formData.family.contact.homePhone}
          onChange={(e) => handleInputChange('family', 'contact', e.target.value, 'homePhone')}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Cell Phone"
          value={formData.family.contact.cellPhone}
          onChange={(e) => handleInputChange('family', 'contact', e.target.value, 'cellPhone')}
        />
      </Grid>
      <Grid item xs={12}>
        <TextField
          fullWidth
          label="Email"
          type="email"
          value={formData.family.contact.email}
          onChange={(e) => handleInputChange('family', 'contact', e.target.value, 'email')}
          required
        />
      </Grid>
    </Grid>
  );

  const renderSacramentSection = (section, title) => (
    <Box sx={{ mt: 3 }}>
      <Typography variant="h6" gutterBottom color="primary">
        {title} Sacraments
      </Typography>
      {['baptized', 'communion', 'confirmed'].map((sacrament) => (
        <Card key={sacrament} sx={{ mb: 2, p: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 2, textTransform: 'capitalize' }}>
            {sacrament === 'baptized' ? 'Baptism' : sacrament === 'communion' ? 'First Communion' : 'Confirmation'}
          </Typography>

          <FormControl component="fieldset" sx={{ mb: 2 }}>
            <FormLabel component="legend">Have they received this sacrament?</FormLabel>
            <RadioGroup
              value={formData[section].sacraments[sacrament].received}
              onChange={(e) => handleSacramentChange(section, sacrament, 'received', e.target.value)}
              row
            >
              <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
              <FormControlLabel value="No" control={<Radio />} label="No" />
            </RadioGroup>
          </FormControl>

          {formData[section].sacraments[sacrament].received === 'Yes' && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Where received?"
                  value={formData[section].sacraments[sacrament].where}
                  onChange={(e) => handleSacramentChange(section, sacrament, 'where', e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="When received?"
                  value={formData[section].sacraments[sacrament].when}
                  onChange={(e) => handleSacramentChange(section, sacrament, 'when', e.target.value)}
                />
              </Grid>
            </Grid>
          )}

          {formData[section].sacraments[sacrament].received === 'No' && (
            <FormControl component="fieldset">
              <FormLabel component="legend">Would they like to receive this sacrament?</FormLabel>
              <RadioGroup
                value={formData[section].sacraments[sacrament].interested}
                onChange={(e) => handleSacramentChange(section, sacrament, 'interested', e.target.value)}
                row
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          )}
        </Card>
      ))}
    </Box>
  );

  const renderPrimaryContact = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="First Name"
          value={formData.primaryContact.firstName}
          onChange={(e) => handleInputChange('primaryContact', 'firstName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Last Name"
          value={formData.primaryContact.lastName}
          onChange={(e) => handleInputChange('primaryContact', 'lastName', e.target.value)}
          required
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <FormControl component="fieldset" required>
          <FormLabel component="legend">Gender</FormLabel>
          <RadioGroup
            value={formData.primaryContact.gender}
            onChange={(e) => handleInputChange('primaryContact', 'gender', e.target.value)}
            row
          >
            <FormControlLabel value="Male" control={<Radio />} label="Male" />
            <FormControlLabel value="Female" control={<Radio />} label="Female" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Date of Birth"
          type="date"
          value={formData.primaryContact.dateOfBirth}
          onChange={(e) => handleInputChange('primaryContact', 'dateOfBirth', e.target.value)}
          InputLabelProps={{ shrink: true }}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Occupation"
          value={formData.primaryContact.occupation}
          onChange={(e) => handleInputChange('primaryContact', 'occupation', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Employer"
          value={formData.primaryContact.employer}
          onChange={(e) => handleInputChange('primaryContact', 'employer', e.target.value)}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControl component="fieldset">
          <FormLabel component="legend">Residency Status</FormLabel>
          <RadioGroup
            value={formData.primaryContact.residency}
            onChange={(e) => handleInputChange('primaryContact', 'residency', e.target.value)}
            row
          >
            <FormControlLabel value="Permanent Resident" control={<Radio />} label="Permanent Resident" />
            <FormControlLabel value="Student" control={<Radio />} label="Student" />
            <FormControlLabel value="Visitor" control={<Radio />} label="Visitor" />
          </RadioGroup>
        </FormControl>
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Primary Language"
          value={formData.primaryContact.primaryLanguage}
          onChange={(e) => handleInputChange('primaryContact', 'primaryLanguage', e.target.value)}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <TextField
          fullWidth
          label="Current Religion"
          value={formData.primaryContact.currentReligion}
          onChange={(e) => handleInputChange('primaryContact', 'currentReligion', e.target.value)}
        />
      </Grid>

      <Grid item xs={12}>
        {renderSacramentSection('primaryContact', 'Primary Contact')}
      </Grid>
    </Grid>
  );

  const renderSpouseAndFamily = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Marriage Information</Typography>
        <FormControl fullWidth>
          <InputLabel>Marital Status</InputLabel>
          <Select
            value={formData.primaryContact.marriage.status}
            onChange={(e) => handleInputChange('primaryContact', 'marriage', e.target.value, 'status')}
            label="Marital Status"
          >
            <MenuItem value="Not Married">Not Married</MenuItem>
            <MenuItem value="Married">Married</MenuItem>
            <MenuItem value="Divorced">Divorced</MenuItem>
            <MenuItem value="Widowed">Widowed</MenuItem>
          </Select>
        </FormControl>
      </Grid>

      {formData.primaryContact.marriage.status === 'Married' && (
        <>
          <Grid item xs={12}>
            <Divider />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Spouse Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse First Name"
              value={formData.spouse.firstName}
              onChange={(e) => handleInputChange('spouse', 'firstName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Last Name"
              value={formData.spouse.lastName}
              onChange={(e) => handleInputChange('spouse', 'lastName', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Spouse Gender</FormLabel>
              <RadioGroup
                value={formData.spouse.gender}
                onChange={(e) => handleInputChange('spouse', 'gender', e.target.value)}
                row
              >
                <FormControlLabel value="Male" control={<Radio />} label="Male" />
                <FormControlLabel value="Female" control={<Radio />} label="Female" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Date of Birth"
              type="date"
              value={formData.spouse.dateOfBirth}
              onChange={(e) => handleInputChange('spouse', 'dateOfBirth', e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Occupation"
              value={formData.spouse.occupation}
              onChange={(e) => handleInputChange('spouse', 'occupation', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Employer"
              value={formData.spouse.employer}
              onChange={(e) => handleInputChange('spouse', 'employer', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Primary Language"
              value={formData.spouse.primaryLanguage}
              onChange={(e) => handleInputChange('spouse', 'primaryLanguage', e.target.value)}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Spouse Current Religion"
              value={formData.spouse.currentReligion}
              onChange={(e) => handleInputChange('spouse', 'currentReligion', e.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Divider />
            <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>Marriage Information</Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Marriage Location"
              value={formData.primaryContact.marriage.location}
              onChange={(e) => handleInputChange('primaryContact', 'marriage', e.target.value, 'location')}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Marriage Date"
              type="date"
              value={formData.primaryContact.marriage.date}
              onChange={(e) => handleInputChange('primaryContact', 'marriage', e.target.value, 'date')}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl component="fieldset">
              <FormLabel component="legend">Married in Catholic Church?</FormLabel>
              <RadioGroup
                value={formData.primaryContact.marriage.catholicChurch}
                onChange={(e) => handleInputChange('primaryContact', 'marriage', e.target.value, 'catholicChurch')}
                row
              >
                <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                <FormControlLabel value="No" control={<Radio />} label="No" />
              </RadioGroup>
            </FormControl>
          </Grid>

          <Grid item xs={12}>
            {renderSacramentSection('spouse', 'Spouse')}
          </Grid>
        </>
      )}

      <Grid item xs={12}>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
          <Typography variant="h6">Children</Typography>
          <Button startIcon={<AddIcon />} onClick={addChild}>Add Child</Button>
        </Box>
        {formData.children.map((child, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={child.firstName}
                    onChange={(e) => {
                      const newChildren = [...formData.children];
                      newChildren[index].firstName = e.target.value;
                      setFormData(prev => ({ ...prev, children: newChildren }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={child.lastName}
                    onChange={(e) => {
                      const newChildren = [...formData.children];
                      newChildren[index].lastName = e.target.value;
                      setFormData(prev => ({ ...prev, children: newChildren }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Gender</FormLabel>
                    <RadioGroup
                      value={child.gender}
                      onChange={(e) => {
                        const newChildren = [...formData.children];
                        newChildren[index].gender = e.target.value;
                        setFormData(prev => ({ ...prev, children: newChildren }));
                      }}
                      row
                    >
                      <FormControlLabel value="Male" control={<Radio />} label="Male" />
                      <FormControlLabel value="Female" control={<Radio />} label="Female" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={child.dateOfBirth}
                    onChange={(e) => {
                      const newChildren = [...formData.children];
                      newChildren[index].dateOfBirth = e.target.value;
                      setFormData(prev => ({ ...prev, children: newChildren }));
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Primary Language"
                    value={child.primaryLanguage}
                    onChange={(e) => {
                      const newChildren = [...formData.children];
                      newChildren[index].primaryLanguage = e.target.value;
                      setFormData(prev => ({ ...prev, children: newChildren }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Religion"
                    value={child.currentReligion}
                    onChange={(e) => {
                      const newChildren = [...formData.children];
                      newChildren[index].currentReligion = e.target.value;
                      setFormData(prev => ({ ...prev, children: newChildren }));
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Child Sacraments
                </Typography>
                {['baptized', 'communion', 'confirmed'].map((sacrament) => (
                  <Card key={sacrament} sx={{ mb: 2, p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, textTransform: 'capitalize' }}>
                      {sacrament === 'baptized' ? 'Baptism' : sacrament === 'communion' ? 'First Communion' : 'Confirmation'}
                    </Typography>

                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <FormLabel component="legend">Have they received this sacrament?</FormLabel>
                      <RadioGroup
                        value={child.sacraments[sacrament].received}
                        onChange={(e) => {
                          const newChildren = [...formData.children];
                          newChildren[index].sacraments[sacrament].received = e.target.value;
                          setFormData(prev => ({ ...prev, children: newChildren }));
                        }}
                        row
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>

                    {child.sacraments[sacrament].received === 'Yes' && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Where received?"
                            value={child.sacraments[sacrament].where}
                            onChange={(e) => {
                              const newChildren = [...formData.children];
                              newChildren[index].sacraments[sacrament].where = e.target.value;
                              setFormData(prev => ({ ...prev, children: newChildren }));
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="When received?"
                            value={child.sacraments[sacrament].when}
                            onChange={(e) => {
                              const newChildren = [...formData.children];
                              newChildren[index].sacraments[sacrament].when = e.target.value;
                              setFormData(prev => ({ ...prev, children: newChildren }));
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {child.sacraments[sacrament].received === 'No' && (
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Would they like to receive this sacrament?</FormLabel>
                        <RadioGroup
                          value={child.sacraments[sacrament].interested}
                          onChange={(e) => {
                            const newChildren = [...formData.children];
                            newChildren[index].sacraments[sacrament].interested = e.target.value;
                            setFormData(prev => ({ ...prev, children: newChildren }));
                          }}
                          row
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Card>
                ))}
              </Box>
            </CardContent>
            <CardActions>
              <IconButton onClick={() => removeChild(index)} color="error">
                <DeleteIcon />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Grid>

      <Grid item xs={12}>
        <Divider />
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', my: 2 }}>
          <Typography variant="h6">Additional Adults</Typography>
          <Button startIcon={<AddIcon />} onClick={addAdult}>Add Adult</Button>
        </Box>
        {formData.additionalAdults.map((adult, index) => (
          <Card key={index} sx={{ mb: 2 }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="First Name"
                    value={adult.firstName}
                    onChange={(e) => {
                      const newAdults = [...formData.additionalAdults];
                      newAdults[index].firstName = e.target.value;
                      setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Last Name"
                    value={adult.lastName}
                    onChange={(e) => {
                      const newAdults = [...formData.additionalAdults];
                      newAdults[index].lastName = e.target.value;
                      setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl component="fieldset">
                    <FormLabel component="legend">Gender</FormLabel>
                    <RadioGroup
                      value={adult.gender}
                      onChange={(e) => {
                        const newAdults = [...formData.additionalAdults];
                        newAdults[index].gender = e.target.value;
                        setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                      }}
                      row
                    >
                      <FormControlLabel value="Male" control={<Radio />} label="Male" />
                      <FormControlLabel value="Female" control={<Radio />} label="Female" />
                    </RadioGroup>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Date of Birth"
                    type="date"
                    value={adult.dateOfBirth}
                    onChange={(e) => {
                      const newAdults = [...formData.additionalAdults];
                      newAdults[index].dateOfBirth = e.target.value;
                      setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                    }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>Relationship</InputLabel>
                    <Select
                      value={adult.relationship}
                      onChange={(e) => {
                        const newAdults = [...formData.additionalAdults];
                        newAdults[index].relationship = e.target.value;
                        setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                      }}
                      label="Relationship"
                    >
                      <MenuItem value="Parent">Parent</MenuItem>
                      <MenuItem value="Aunt/Uncle">Aunt/Uncle</MenuItem>
                      <MenuItem value="Cousin">Cousin</MenuItem>
                      <MenuItem value="Niece/Nephew">Niece/Nephew</MenuItem>
                      <MenuItem value="Other">Other</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Primary Language"
                    value={adult.primaryLanguage}
                    onChange={(e) => {
                      const newAdults = [...formData.additionalAdults];
                      newAdults[index].primaryLanguage = e.target.value;
                      setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                    }}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    fullWidth
                    label="Current Religion"
                    value={adult.currentReligion}
                    onChange={(e) => {
                      const newAdults = [...formData.additionalAdults];
                      newAdults[index].currentReligion = e.target.value;
                      setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                    }}
                  />
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Typography variant="h6" gutterBottom color="primary">
                  Adult Sacraments
                </Typography>
                {['baptized', 'communion', 'confirmed'].map((sacrament) => (
                  <Card key={sacrament} sx={{ mb: 2, p: 2 }}>
                    <Typography variant="subtitle1" sx={{ mb: 2, textTransform: 'capitalize' }}>
                      {sacrament === 'baptized' ? 'Baptism' : sacrament === 'communion' ? 'First Communion' : 'Confirmation'}
                    </Typography>

                    <FormControl component="fieldset" sx={{ mb: 2 }}>
                      <FormLabel component="legend">Have they received this sacrament?</FormLabel>
                      <RadioGroup
                        value={adult.sacraments[sacrament].received}
                        onChange={(e) => {
                          const newAdults = [...formData.additionalAdults];
                          newAdults[index].sacraments[sacrament].received = e.target.value;
                          setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                        }}
                        row
                      >
                        <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                        <FormControlLabel value="No" control={<Radio />} label="No" />
                      </RadioGroup>
                    </FormControl>

                    {adult.sacraments[sacrament].received === 'Yes' && (
                      <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Where received?"
                            value={adult.sacraments[sacrament].where}
                            onChange={(e) => {
                              const newAdults = [...formData.additionalAdults];
                              newAdults[index].sacraments[sacrament].where = e.target.value;
                              setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="When received?"
                            value={adult.sacraments[sacrament].when}
                            onChange={(e) => {
                              const newAdults = [...formData.additionalAdults];
                              newAdults[index].sacraments[sacrament].when = e.target.value;
                              setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                            }}
                          />
                        </Grid>
                      </Grid>
                    )}

                    {adult.sacraments[sacrament].received === 'No' && (
                      <FormControl component="fieldset">
                        <FormLabel component="legend">Would they like to receive this sacrament?</FormLabel>
                        <RadioGroup
                          value={adult.sacraments[sacrament].interested}
                          onChange={(e) => {
                            const newAdults = [...formData.additionalAdults];
                            newAdults[index].sacraments[sacrament].interested = e.target.value;
                            setFormData(prev => ({ ...prev, additionalAdults: newAdults }));
                          }}
                          row
                        >
                          <FormControlLabel value="Yes" control={<Radio />} label="Yes" />
                          <FormControlLabel value="No" control={<Radio />} label="No" />
                        </RadioGroup>
                      </FormControl>
                    )}
                  </Card>
                ))}
              </Box>
            </CardContent>
            <CardActions>
              <IconButton onClick={() => removeAdult(index)} color="error">
                <DeleteIcon />
              </IconButton>
            </CardActions>
          </Card>
        ))}
      </Grid>
    </Grid>
  );

  const renderReview = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Typography variant="h6" gutterBottom>Review Registration</Typography>
        <Typography variant="body1">
          <strong>Family:</strong> {formData.family.firstNames} {formData.family.lastName}
        </Typography>
        <Typography variant="body1">
          <strong>Email:</strong> {formData.family.contact.email}
        </Typography>
        <Typography variant="body1">
          <strong>Primary Contact:</strong> {formData.primaryContact.firstName} {formData.primaryContact.lastName}
        </Typography>
        {formData.children.length > 0 && (
          <Typography variant="body1">
            <strong>Children:</strong> {formData.children.length}
          </Typography>
        )}
        {formData.additionalAdults.length > 0 && (
          <Typography variant="body1">
            <strong>Additional Adults:</strong> {formData.additionalAdults.length}
          </Typography>
        )}
      </Grid>
    </Grid>
  );

  const getStepContent = (step) => {
    switch (step) {
      case 0:
        return renderFamilyInfo();
      case 1:
        return renderPrimaryContact();
      case 2:
        return renderSpouseAndFamily();
      case 3:
        return renderReview();
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {registration ? 'Edit Parish Registration' : 'New Parish Registration'}
      </DialogTitle>

      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {getStepContent(activeStep)}
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={onClose} startIcon={<CancelIcon />}>
          Cancel
        </Button>
        {activeStep > 0 && (
          <Button onClick={() => setActiveStep(prev => prev - 1)}>
            Back
          </Button>
        )}
        {activeStep < steps.length - 1 ? (
          <Button variant="contained" onClick={() => setActiveStep(prev => prev + 1)}>
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={loading}
            startIcon={<SaveIcon />}
          >
            {loading ? 'Saving...' : 'Save Registration'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ParishRegistrationForm;